import { useMutation } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input } from '@/components/ui';
import { gisApi, type GeocodeHit } from '@/lib/api';
import { cn } from '@/lib/cn';

interface GISGeocodeSearchProps {
  onSubmit: (geom: GeoJSON.Geometry, label: string) => void;
  pending: boolean;
  /**
   * When non-empty, the search box opens pre-filled with this query and
   * fires the Nominatim search immediately. Used by the failed-candidates
   * "Search address" flow so the analyst lands on results without typing.
   */
  initialQuery?: string;
}

export function GISGeocodeSearch({
  onSubmit,
  pending,
  initialQuery,
}: GISGeocodeSearchProps) {
  const { t } = useTranslation('studio');
  const [q, setQ] = useState(initialQuery ?? '');
  const [selected, setSelected] = useState<GeocodeHit | null>(null);

  const search = useMutation({
    mutationFn: (term: string) => gisApi.geocode(term),
    onSuccess: () => setSelected(null),
  });

  // When the parent hands us a non-empty initial query, sync local state
  // and auto-run the search. Re-fires every time the query changes so
  // jumping between two failed candidates re-searches each time.
  useEffect(() => {
    const trimmed = (initialQuery ?? '').trim();
    if (!trimmed) return;
    setQ(trimmed);
    if (trimmed.length >= 2) search.mutate(trimmed);
    // We intentionally exclude `search` from deps — `useMutation` returns
    // a fresh ref on every render and would loop infinitely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim().length >= 2) search.mutate(q.trim());
        }}
        className="flex gap-2"
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('gis.editor.geocode.placeholder')}
          minLength={2}
          required
        />
        <Button type="submit" disabled={search.isPending || q.trim().length < 2}>
          <Search className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('gis.editor.geocode.search')}
        </Button>
      </form>

      {search.isError && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
          {t('gis.editor.geocode.rateLimit')}
        </p>
      )}

      {search.data && search.data.length === 0 && (
        <p className="text-small text-slate-500">{t('gis.editor.geocode.noResults')}</p>
      )}

      {search.data && search.data.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded border border-slate-200">
          {search.data.map((hit, i) => (
            <li key={`${hit.lat}-${hit.lon}-${i}`}>
              <button
                type="button"
                onClick={() => setSelected(hit)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50',
                  selected === hit && 'bg-brand-50',
                )}
              >
                <span className="flex-1 text-body text-slate-900">{hit.label}</span>
                <span className="font-mono text-small text-slate-500">
                  {hit.lat.toFixed(4)}, {hit.lon.toFixed(4)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() =>
            selected &&
            onSubmit(
              { type: 'Point', coordinates: [selected.lon, selected.lat] },
              selected.label,
            )
          }
          disabled={!selected || pending}
        >
          {t('gis.editor.save')}
        </Button>
      </div>
    </div>
  );
}
